import api from 'api';
import { createSlice } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import { IAddClientUserRequest, IEditClientUserRequest } from 'components/sidemodal/add-user';
import { IAddress } from '../../helpers/types';
import { IVehicle as IVehicleDetails } from './vehicleSlice';
import { IOrganizations } from './organizationSlice';
import { IClientAssignedVehicleOptionItem, IClientUserListItem } from 'interfaces/users-interface';

export interface IGetUserAPIParams {
  organization_id?: string;
  searchString?: string;
  role_id?: string;
  company_id?: string;
  offset?: number;
  limit?: number;
}
export interface IClientUser {
  _id?: string;
  id?: string;
  name: string;
  surname: string;
  email: string;
  country_code: string;
  mobile_no: string;
  date_of_birth: string;
  address: IAddress | null;
  dni_no: string;
  dni_exp_date: string;
  license_no: string;
  license_exp_date: string;
  billing_address: IAddress | null;
  policy_no: string;
  policy_exp_date: string;
  type_of_purchase_ids: string[];
  company_details?: {
    _id?: string;
    id?: string;
    name: string;
    activity: string;
    fiscal_number: string;
    headquarters: IAddress;
    other_stations: IAddress[];
  }[];
  vehicle_details?: IVehicleDetails[];
  organization_id?: string;
  organizationDetails?: IOrganizations;
}
interface IUserState {
  totalCount: number;
  users: IClientUserListItem[];
  editUser: IClientUserListItem | null;
  vehicles: IClientAssignedVehicleOptionItem[];
  isAddUserModalOpenFromUserModule: boolean;
}
interface IClientUserResponse {
  response: IClientUserListItem[];
  record_count: number;
}
const initialState: IUserState = {
  totalCount: 0,
  users: [],
  editUser: null,
  vehicles: [],
  isAddUserModalOpenFromUserModule: true,
};
const userSlice = createSlice({
  name: 'User',
  initialState: initialState,
  reducers: {
    setUsers: (state, { payload }: { payload: IClientUserResponse }) => {
      state.users = [...payload?.response];
      state.totalCount = payload?.record_count;
    },
    setEditUser: (state, { payload }: { payload: IClientUserListItem | null }) => {
      state.editUser = payload;
    },
    setVehicles: (state, { payload }: { payload: IClientAssignedVehicleOptionItem[] }) => {
      state.vehicles = payload;
    },
    setAddUserModalFlag: (state, { payload }: { payload: boolean }) => {
      state.isAddUserModalOpenFromUserModule = payload;
    },
  },
});

export const getSuperAdminUsers =
  (
    payload: {
      params?: IGetUserAPIParams;
      searchString?: string;
      offset?: number;
      limit?: number;
    },
    signal?: AbortSignal,
  ) =>
  async (dispatch: Dispatch) => {
    try {
      const { params, searchString, offset, limit } = payload;
      const queryString = `?offset=${offset ?? 0}&limit=${limit ?? 20}${
        searchString ? `&searchString=${encodeURIComponent(searchString)}` : ''
      }`;

      const { data: responseData } = await api.get(`/admin/users${queryString}`, { signal });
      dispatch(setUsers(responseData?.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const getDealerAdminUsers =
  (payload: { params?: IGetUserAPIParams }, signal?: AbortSignal) => async (dispatch: Dispatch) => {
    try {
      const { params } = payload;
      const { data: responseData } = await api.get('/admin/org/users', { params, signal });
      dispatch(setUsers(responseData?.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const addUser =
  (payload: { request?: IAddClientUserRequest }) => async (dispatch: Dispatch) => {
    try {
      const { request } = payload;
      const { data: responseData } = await api.post('/admin/users', { ...request });
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const setEditUserData =
  (payload: { request: IClientUserListItem | null }) => (dispatch: Dispatch) => {
    try {
      const { request } = payload;
      dispatch(setEditUser(request));
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const editUserAPI =
  (payload: { request?: IEditClientUserRequest; userId: string }) => async (dispatch: Dispatch) => {
    try {
      const { request, userId } = payload;
      const { data: responseData } = await api.post(`/admin/users/${userId}`, { ...request });
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const deleteUser = (payload: { userId: string }) => async (dispatch: Dispatch) => {
  try {
    const { userId } = payload;
    const { data: responseData } = await api.delete(`/admin/user/${userId}`);
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getVehiclesList =
  (payload: {
    params?: { company_id?: string[]; organization_id?: string; is_from_book_workshop?: boolean };
  }) =>
  async (dispatch: Dispatch) => {
    try {
      const { params } = payload;
      const { data: responseData } = await api.get('/admin/vehicle/getVehicleNames', { params });
      dispatch(setVehicles(responseData?.data?.vehicleList));
      return responseData?.data?.vehicleList;
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const { setUsers, setEditUser, setVehicles, setAddUserModalFlag } = userSlice.actions;
export default userSlice.reducer;
